import { useCallback } from 'react';

import { useCanUserConnectToOrion } from '@/modules/assetProviderIntegrations/orion/hooks/useIsOrionEnabled';
import {
  getOrionIntegrationLoginPath,
  getOrionIntegrationLogoutPath,
} from '@/modules/assetProviderIntegrations/orion/utils/orionAuthUtils';
import { ASSET_INTEGRATION_PROVIDER_DISPLAY_NAMES } from '@/modules/assetProviderIntegrations/shared/constants';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { IntegrationProvider } from '@/types/schema';
import { getPublicImageUrl } from '@/utils/staticFiles';

import { IntegrationRow } from '../IntegrationRow';

interface OrionCardProps {
  userAccountLinked: boolean;
  externalUserName?: string | null;
}

export function OrionCard({
  userAccountLinked,
  externalUserName,
}: OrionCardProps) {
  const canEditHouseholdData = useCanEditHouseholdData();
  const canUserConnectToOrion = useCanUserConnectToOrion();
  const { assetIntegrationsEnabled } = useTenantDetailsContext();
  const currentUser = useCurrentUser();
  const isExpired =
    currentUser?.userExpiredIntegrationNames?.includes(
      ASSET_INTEGRATION_PROVIDER_DISPLAY_NAMES.ORION
    ) ?? false;

  const getOrionButtonProps = useCallback(() => {
    if (
      !canEditHouseholdData ||
      !assetIntegrationsEnabled ||
      !canUserConnectToOrion
    ) {
      return {
        disabled: true,
        href: '#',
        children: 'Link account',
      };
    }

    const currentPageURL = getCompletePathFromRouteKey('USER_SETTINGS', {});
    if (userAccountLinked) {
      return {
        href: getOrionIntegrationLogoutPath({ redirect: currentPageURL }),
        children: 'Unlink',
        method: 'post' as const,
      };
    } else {
      return {
        href: getOrionIntegrationLoginPath({ redirect: currentPageURL }),
        children: 'Link account',
      };
    }
  }, [
    canEditHouseholdData,
    assetIntegrationsEnabled,
    canUserConnectToOrion,
    userAccountLinked,
  ]);

  return (
    <IntegrationRow
      integrationEnabled={userAccountLinked}
      isExpired={isExpired}
      externalUserName={externalUserName}
      description={
        !canUserConnectToOrion ? 'Contact your administrator' : undefined
      }
      logo={
        <img
          alt="Orion"
          height={24}
          src={getPublicImageUrl('/orionLogo.jpg')}
        />
      }
      buttonProps={getOrionButtonProps()}
      integrationProvider={IntegrationProvider.Orion}
    />
  );
}
